/**
 * ArticleFormModal — text-only create/edit article form.
 * Create: sends isPublished from the "Publish immediately" checkbox (default ON).
 * Edit (PUT): partial update — does NOT send isPublished (backend ignores it on PUT;
 *   publish/unpublish are handled by separate buttons in My Articles).
 * No image upload — text only (per requirement, avoids /media/upload dependency).
 */
import React, { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { GovButton } from '@/components/layout/GovLayout'

interface Category {
  id: number
  name: string
}

export interface Article {
  id: number
  title: string
  content: string
  excerpt?: string
  category?: Category
  author?: { id: number; firstName: string; lastName: string }
  tags?: string[]
  isFeatured: boolean
  isPublished: boolean
  publishedAt?: string
  createdAt: string
}

interface ArticleFormModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  categories: Category[]
  editingArticle: Article | null
  isAdmin: boolean
}

const ArticleFormModal: React.FC<ArticleFormModalProps> = ({
  open, onClose, onSuccess, categories, editingArticle, isAdmin
}) => {
  const isEdit = !!editingArticle
  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [publishNow, setPublishNow] = useState(true)
  const [isFeatured, setIsFeatured] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Prefill when opening (create vs edit)
  useEffect(() => {
    if (!open) return
    setError(null)
    if (editingArticle) {
      setTitle(editingArticle.title || '')
      setCategoryId(editingArticle.category?.id || '')
      setExcerpt(editingArticle.excerpt || '')
      setContent(editingArticle.content || '')
      setTagsInput((editingArticle.tags || []).join(', '))
      setIsFeatured(!!editingArticle.isFeatured)
      setPublishNow(!!editingArticle.isPublished)
    } else {
      setTitle('')
      setCategoryId(categories[0]?.id || '')
      setExcerpt('')
      setContent('')
      setTagsInput('')
      setIsFeatured(false)
      setPublishNow(true)
    }
  }, [open, editingArticle, categories])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Client-side validation (DTO has no @NotBlank, so the FE must guard)
    if (!title.trim()) { setError('Title is required'); return }
    if (!content.trim()) { setError('Content is required'); return }
    if (!categoryId) { setError('Please select a category'); return }

    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0)

    const payload: Record<string, unknown> = {
      title: title.trim(),
      content: content.trim(),
      excerpt: excerpt.trim() || undefined,
      categoryId,
      tags,
      isFeatured,
    }
    // Only send isPublished on CREATE (backend ignores it on PUT)
    if (!isEdit) payload.isPublished = publishNow

    setLoading(true)
    try {
      if (isEdit && editingArticle) {
        await api.put(`/knowledge/articles/${editingArticle.id}`, payload)
      } else {
        await api.post('/knowledge/articles', payload)
      }
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save article')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Edit Article' : 'Create Article'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter article title"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Select a category</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
            <input
              type="text"
              value={excerpt}
              onChange={e => setExcerpt(e.target.value)}
              maxLength={300}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Short summary (optional)"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              required
              rows={10}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              placeholder="Write your article here (text only)..."
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <input
              type="text"
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Comma-separated, e.g. awareness, prevention"
            />
          </div>

          {/* Options */}
          <div className="flex flex-col gap-2 pt-2">
            {!isEdit && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={publishNow}
                  onChange={e => setPublishNow(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Publish immediately
              </label>
            )}
            {isEdit && (
              <p className="text-xs text-gray-500">
                Publish/unpublish is managed from the My Articles tab.
              </p>
            )}
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={e => setIsFeatured(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Mark as featured (admin only)
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <GovButton variant="ghost" onClick={onClose} type="button">
              Cancel
            </GovButton>
            <GovButton disabled={loading} type="submit">
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </span>
              ) : (isEdit ? 'Save Changes' : 'Create Article')}
            </GovButton>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ArticleFormModal